import { token } from '@/infrastructure/container';
import { InvariantError } from '@/domain/errors';

export type Labels = Readonly<Record<string, string>>;

// The catalog: every metric this app emits, described in one place. The map doubles as the
// complete list of what is observable, and its keys type IMetrics, so a typo'd or uncatalogued
// name is a red typecheck rather than a runtime surprise.
//
// Everything here is emitted at a seam that exists today. Add a name when you add the call site,
// not before: a catalogued metric nothing records is the same lie as an invariant nothing enforces.
export const APP_METRICS = {
  app_bus_published_total: 'Bus events published, labelled by event name',
  celeriant_ops_total: 'Celeriant client operations, labelled by op and outcome',
  celeriant_op_duration_ms: 'Celeriant client operation latency in ms, labelled by op',
} as const satisfies Record<string, string>;

export type MetricName = keyof typeof APP_METRICS;

export interface IMetrics {
  counter(name: MetricName, labels?: Labels, by?: number): void;
  gauge(name: MetricName, value: number, labels?: Labels): void;
  histogram(name: MetricName, value: number, labels?: Labels): void;
}

export const TOKEN_IMetrics = token<IMetrics>('IMetrics');

// ms-precision buckets: this app cares about interaction and gateway latency, not microseconds.
const BUCKETS_MS = [1, 5, 10, 25, 50, 100, 250, 500, 1000, 2500] as const;

interface Histogram {
  counts: number[]; // cumulative, one per bucket plus one +Inf slot
  sum: number;
  count: number;
}

// Prometheus label-value escaping. Without it a value containing '"' or '\n' merges distinct
// series and can forge whole exposition lines, which is a silently wrong oracle.
function escapeValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
}

// Keys are sorted so label order at the call site never splits a series. '' means no labels.
function labelsKey(labels?: Labels): string {
  if (labels === undefined) return '';
  return Object.keys(labels)
    .sort()
    .map((k) => `${k}="${escapeValue(labels[k]!)}"`)
    .join(',');
}

function sample(name: string, key: string, value: number): string {
  return key === '' ? `${name} ${value}` : `${name}{${key}} ${value}`;
}

function bucketLine(name: string, key: string, le: string, count: number): string {
  const all = key === '' ? `le="${le}"` : `${key},le="${le}"`;
  return `${name}_bucket{${all}} ${count}`;
}

function bySeriesKey<T>(series: Map<string, T> | undefined): [string, T][] {
  return series === undefined ? [] : [...series.entries()].sort(([a], [b]) => a.localeCompare(b));
}

// Method params are `string`, wider than IMetrics' MetricName: the registry is generic over any
// catalog (tests bring their own), and the narrow typing guards the app's call sites.
export class MetricsRegistry implements IMetrics {
  private counters = new Map<string, Map<string, number>>();
  private gauges = new Map<string, Map<string, number>>();
  private histograms = new Map<string, Map<string, Histogram>>();

  constructor(private descriptions: Readonly<Record<string, string>>) {}

  counter(name: string, labels?: Labels, by = 1): void {
    const series = this.series(this.counters, name);
    const key = labelsKey(labels);
    series.set(key, (series.get(key) ?? 0) + by);
  }

  gauge(name: string, value: number, labels?: Labels): void {
    this.series(this.gauges, name).set(labelsKey(labels), value);
  }

  histogram(name: string, value: number, labels?: Labels): void {
    const series = this.series(this.histograms, name);
    const key = labelsKey(labels);
    let h = series.get(key);
    if (h === undefined) {
      h = { counts: new Array<number>(BUCKETS_MS.length + 1).fill(0), sum: 0, count: 0 };
      series.set(key, h);
    }
    h.sum += value;
    h.count++;
    for (let i = 0; i < BUCKETS_MS.length; i++) if (value <= BUCKETS_MS[i]!) h.counts[i]!++;
    h.counts[BUCKETS_MS.length]!++;
  }

  // Typed reads: tests assert on values, never on parsed exposition text.
  counterValue(name: string, labels?: Labels): number {
    return this.counters.get(name)?.get(labelsKey(labels)) ?? 0;
  }

  gaugeValue(name: string, labels?: Labels): number | undefined {
    return this.gauges.get(name)?.get(labelsKey(labels));
  }

  histogramStats(name: string, labels?: Labels): { count: number; sum: number } | undefined {
    const h = this.histograms.get(name)?.get(labelsKey(labels));
    return h === undefined ? undefined : { count: h.count, sum: h.sum };
  }

  /** Prometheus text exposition: the artifact an agent (or a human) reads after a run. */
  render(): string {
    const names = [
      ...new Set([...this.counters.keys(), ...this.gauges.keys(), ...this.histograms.keys()]),
    ].sort();
    const lines: string[] = [];
    for (const name of names) {
      lines.push(`# HELP ${name} ${this.descriptions[name]}`);
      for (const [key, value] of bySeriesKey(this.counters.get(name)))
        lines.push(sample(name, key, value));
      for (const [key, value] of bySeriesKey(this.gauges.get(name)))
        lines.push(sample(name, key, value));
      for (const [key, h] of bySeriesKey(this.histograms.get(name))) {
        for (let i = 0; i < BUCKETS_MS.length; i++)
          lines.push(bucketLine(name, key, String(BUCKETS_MS[i]), h.counts[i]!));
        lines.push(bucketLine(name, key, '+Inf', h.counts[BUCKETS_MS.length]!));
        lines.push(sample(`${name}_sum`, key, h.sum));
        lines.push(sample(`${name}_count`, key, h.count));
      }
    }
    return lines.join('\n') + '\n';
  }

  private series<T>(kind: Map<string, Map<string, T>>, name: string): Map<string, T> {
    if (!(name in this.descriptions)) {
      throw new InvariantError(
        `Metric "${name}" has no description. Add it to the catalog handed to MetricsRegistry ` +
          `(APP_METRICS in src/services/Metrics.ts).`,
      );
    }
    let series = kind.get(name);
    if (series === undefined) {
      series = new Map();
      kind.set(name, series);
    }
    return series;
  }
}

// For tests that do not assert on metrics. Never throws, whatever name reaches it.
export class NoopMetrics implements IMetrics {
  counter(): void {}
  gauge(): void {}
  histogram(): void {}
}
